import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { ehrMappingKeys } from './ehrMappingsApi';
import type {
  ApplyPreview,
  ApplyResult,
  ApplyToResidentsRequest,
  MapThisCount,
  MappingHealthReport,
  MapThisPreview,
  MapThisQueue,
  MapThisRefusal,
  MapThisRequest,
  MapThisResident,
  MapThisSaveResult,
} from '../types/ehrMapThis';

// React Query hooks for the "Map this" pop-up and the mapping health report (EhrMapThisController).

export const mapThisKeys = {
  all: ['ehrMapThis'] as const,
  queue: (facilityId: number) => [...mapThisKeys.all, 'queue', facilityId] as const,
  residents: (facilityId: number, text: string, part: number) => [...mapThisKeys.all, 'residents', facilityId, part, text] as const,
  preview: (facilityId: number, body: string) => [...mapThisKeys.all, 'preview', facilityId, body] as const,
  health: (facilityId: number) => [...mapThisKeys.all, 'health', facilityId] as const,
  count: (facilityId: number) => [...mapThisKeys.all, 'count', facilityId] as const,
  applyPreview: (facilityId: number, ids: string) => [...mapThisKeys.all, 'applyPreview', facilityId, ids] as const,
};

/** True when the API build has no such route yet (an API slot older than the website): the feature stays hidden. */
export function isMissingRoute(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

/** The waiting phrases. Quiet on a 404 (the slot's build predates the route): no version alarm, no retries. */
export function useMapThisQueue(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: mapThisKeys.queue(facilityId),
    queryFn: async () =>
      (await apiClient.get<MapThisQueue>(API.ehrMapThisQueue(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    retry: (count, error) => !isMissingRoute(error) && count < 2,
  });
}

export function useMapThisResidents(facilityId: number, text: string, part: number, enabled: boolean) {
  return useQuery({
    queryKey: mapThisKeys.residents(facilityId, text, part),
    queryFn: async () => (await apiClient.get<MapThisResident[]>(API.ehrMapThisResidents(facilityId, text, part))).data,
    enabled: enabled && facilityId > 0 && text.length > 0,
  });
}

/** What saving would do. Keyed on the request, so going back to an earlier choice is instant. */
export function useMapThisPreview(facilityId: number, request: MapThisRequest | null) {
  const body = request ? JSON.stringify(request) : '';
  return useQuery({
    queryKey: mapThisKeys.preview(facilityId, body),
    queryFn: async () => (await apiClient.post<MapThisPreview>(API.ehrMapThisPreview(facilityId), request)).data,
    enabled: facilityId > 0 && request != null && request.mappings.length > 0,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useSaveMapThis(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: MapThisRequest) =>
      (await apiClient.post<MapThisSaveResult>(API.ehrMapThisSave(facilityId), request)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.mappings(facilityId) });
      qc.invalidateQueries({ queryKey: mapThisKeys.health(facilityId) });
      qc.invalidateQueries({ queryKey: mapThisKeys.count(facilityId) });
      qc.removeQueries({ queryKey: [...mapThisKeys.all, 'preview', facilityId] });
    },
  });
}

/** The 409 body of a refused save, when there is one. */
export function mapThisRefusal(error: unknown): MapThisRefusal | null {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 409 || !response.data || typeof response.data !== 'object') return null;
  return response.data as MapThisRefusal;
}

export function useMappingHealth(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: mapThisKeys.health(facilityId),
    // An API slot older than the website answers 404: the section says it couldn't load, no version alarm.
    queryFn: async () =>
      (await apiClient.get<MappingHealthReport>(API.ehrMapThisHealth(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    retry: (count, error) => !isMissingRoute(error) && count < 2,
  });
}

/** How often the "waiting for mapping" badge asks again (the API keeps the count a minute per facility). */
export const MAP_COUNT_REFRESH_MS = 2 * 60 * 1000;

/**
 * The "waiting for mapping" badge count: phrases holding messages first, then the rest. Quiet on a 404
 * (an API slot older than the website): the badge just stays away.
 */
export function useMapThisCount(facilityId: number, enabled = true, queryClient?: QueryClient) {
  return useQuery({
    queryKey: mapThisKeys.count(facilityId),
    queryFn: async () =>
      (await apiClient.get<MapThisCount>(API.ehrMapThisCount(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    refetchInterval: MAP_COUNT_REFRESH_MS,
    staleTime: 60 * 1000,
    retry: (count, error) => !isMissingRoute(error) && count < 1,
  }, queryClient);
}

/** Who saved mappings would apply to among the residents in house, and what would change. Nothing is changed. */
export function useApplyPreview(facilityId: number, mappingIds: number[], enabled = true) {
  const ids = [...mappingIds].sort((a, b) => a - b).join(',');
  return useQuery({
    queryKey: mapThisKeys.applyPreview(facilityId, ids),
    queryFn: async () =>
      (await apiClient.post<ApplyPreview>(API.ehrMapThisApplyPreview(facilityId), { mappingIds } satisfies ApplyToResidentsRequest,
        { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0 && mappingIds.length > 0,
    staleTime: 15 * 1000,
    retry: (count, error) => !isMissingRoute(error) && count < 1,
  });
}

/** Applies saved mappings to the residents confirmed from the preview. */
export function useApplyToResidents(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: ApplyToResidentsRequest) =>
      (await apiClient.post<ApplyResult>(API.ehrMapThisApply(facilityId), { ...request, confirmed: true })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...mapThisKeys.all, 'applyPreview', facilityId] });
    },
  });
}
